import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Params } from "@angular/router";
import { Observable, catchError, forkJoin, map, of, switchMap } from "rxjs";

import { deFondo } from "src/app/security/helper/peticion-de-fondo";
import { UrlConstantes } from "src/app/utilerias/constantes/urlconstantes";
import { TicketsEstatus } from "src/app/utilerias/enums/tickets-estatus.enum";

/** Las tres cosas que se piden en «Nueva solicitud». */
export type TipoSolicitud = "ticket" | "ia" | "mantenimiento";

/** Cómo va lo que uno ya pidió de un tipo. */
export interface Seguimiento {
  /** Las que siguen caminando del lado de quien atiende. */
  enCurso: number;
  /** Las que esperan algo de quien pidió (su visto bueno o su respuesta): su número, en el orden de su lista. */
  esperan: number[];
}

/** A dónde lleva un enlace del concentrador: la ruta y sus parámetros. */
export interface Destino {
  ruta: (string | number)[];
  consulta: Params | null;
}

/** Lo que dice el acceso de un tipo en «¿Ya pediste algo?». */
export interface Marca {
  /** `consultando` mientras llega la respuesta; `sin-dato` si no se pudo saber. */
  estado: "consultando" | "sin-dato" | "listo";
  /** «2 en curso»; vacío si no hay ninguna caminando. */
  enCurso: string;
  /** «1 espera tu visto bueno»; vacío si ninguna espera algo de uno. */
  pide: string;
  /** A dónde lleva ese aviso: a la que espera o, si no se puede abrir una sola, a donde se ven juntas. */
  aLaQueEspera: Destino | null;
}

export const CONSULTANDO: Marca = { estado: "consultando", enCurso: "", pide: "", aLaQueEspera: null };
const SIN_DATO: Marca = { estado: "sin-dato", enCurso: "", pide: "", aLaQueEspera: null };

/** Qué espera de uno cada tipo, con una y con varias. */
const PIDE: Record<TipoSolicitud, [string, string]> = {
  ticket: ["espera tu visto bueno", "esperan tu visto bueno"],
  ia: ["espera tu respuesta", "esperan tu respuesta"],
  mantenimiento: ["espera tu visto bueno", "esperan tu visto bueno"],
};

/** El tablero «Mis tickets» y el detalle de uno, las dos pantallas del módulo de Tickets. */
const RUTA_MIS_TICKETS = "/TICKETS/Cte";
const RUTA_DETALLE_TICKET = "/TICKETS/DetalleWidget";

/**
 * A dónde lleva el aviso de lo que espera algo de uno (pedido de Norman,
 * 12/10/2026: «que el aviso ámbar lleve directo a la que espera»):
 *   - Tickets: con uno solo, su detalle (ahí están «Vo.Bo.» y «Reabrir»);
 *     con varios, su tablero, que los junta en «Atendidas».
 *   - Mantenimiento: con una sola, su detalle (`?solicitud=`), donde se
 *     confirma o se reabre; con varias, su lista, que las pone arriba.
 *   - IA: su lista con la primera que espera respuesta a la vista.
 */
function destinoDeLoQueEspera(tipo: TipoSolicitud, esperan: number[]): Destino | null {
  if (!esperan.length) {
    return null;
  }
  // IA trae a la vista la primera; Tickets y Mantenimiento abren una solo si es la única.
  const candidatas = tipo === "ia" || esperan.length === 1 ? esperan : [];
  const cual = candidatas.find((id) => id > 0) ?? 0;
  if (tipo === "ticket") {
    return { ruta: cual > 0 ? [RUTA_DETALLE_TICKET, cual] : [RUTA_MIS_TICKETS], consulta: null };
  }
  return { ruta: [], consulta: cual > 0 ? { ver: tipo, solicitud: cual } : { ver: tipo } };
}

/** El seguimiento de un tipo, dicho en palabras y con el destino de su aviso. */
export function marca(tipo: TipoSolicitud, s: Seguimiento | null): Marca {
  if (!s) {
    return SIN_DATO;
  }
  const cuantas = s.esperan.length;
  const [una, varias] = PIDE[tipo];
  const espera = cuantas === 1 ? una : varias;
  return {
    estado: "listo",
    enCurso: s.enCurso > 0 ? `${s.enCurso} en curso` : "",
    pide: cuantas > 0 ? `${cuantas} ${espera}` : "",
    aLaQueEspera: destinoDeLoQueEspera(tipo, s.esperan),
  };
}

type TipoPropio = Exclude<TipoSolicitud, "ticket">;

/** Una solicitud de IA o de Mantenimiento, con lo que aquí se lee de ella. */
interface SolicitudPropia {
  idSolicitudIa?: number;
  idSolicitud?: number;
  estatus?: string;
}

/** De IA y de Mantenimiento: su lista, los estatus que siguen caminando, el que espera algo de quien pidió y su número. */
const PROPIAS: Record<TipoPropio, { url: string; enCurso: string[]; pendiente: string; id: "idSolicitudIa" | "idSolicitud" }> = {
  // «Falta información»: quien evalúa preguntó algo y se contesta con «Responder».
  ia: {
    url: UrlConstantes.SOLICITUDES_IA_MIAS,
    enCurso: ["SOLICITADA", "EN_EVALUACION"],
    pendiente: "INFORMACION_REQUERIDA",
    id: "idSolicitudIa",
  },
  // «Resuelta»: quien la pidió confirma o la reabre.
  mantenimiento: {
    url: UrlConstantes.MANTENIMIENTO_MIAS,
    enCurso: ["SOLICITADA", "EN_ATENCION", "EN_ESPERA"],
    pendiente: "RESUELTA",
    id: "idSolicitud",
  },
};

interface ListaPropia {
  codigoError?: unknown;
  solicitudes?: SolicitudPropia[];
}

interface AreasUsuario {
  list?: { id?: number | null }[];
}

interface TicketsPorEstatus {
  pCodRetorno?: string | number | null;
  listaDeTiCkets?: { id_ticket?: number }[];
}

/**
 * Cómo va lo que uno ya pidió, para marcar los accesos de «¿Ya pediste
 * algo?» (pedido de Norman, 12/10/2026: «si tiene solicitudes, que se
 * marquen»).
 *
 * Solo LEE, con las mismas consultas que usa la lista de cada módulo, y no
 * toca ninguno. Van de fondo (sin el velo de «Cargando…»): las lanza la
 * pantalla, no el usuario. Si una falla, ese acceso sale sin marcas y lo
 * demás sigue igual.
 */
@Injectable()
export class SeguimientoSolicitudes {
  constructor(private readonly http: HttpClient) {}

  /** Cómo va lo de ese tipo; `null` si no se pudo saber. */
  de(tipo: TipoSolicitud): Observable<Seguimiento | null> {
    const consulta = tipo === "ticket" ? this.tickets() : this.propias(tipo);
    return consulta.pipe(catchError(() => of(null)));
  }

  private pide<T>(url: string, cuerpo: unknown): Observable<T> {
    return this.http.post<T>(url, cuerpo, { context: deFondo() });
  }

  private propias(tipo: TipoPropio): Observable<Seguimiento> {
    const { url, enCurso, pendiente, id } = PROPIAS[tipo];
    return this.pide<ListaPropia | null>(url, {}).pipe(
      map((res) => {
        // Sus errores (y la sesión caducada) viajan en un 200 con codigoError.
        if (!res || res.codigoError) {
          throw new Error("La lista no llegó");
        }
        const lista = res.solicitudes ?? [];
        return {
          enCurso: lista.filter((s) => enCurso.includes(s.estatus ?? "")).length,
          esperan: lista.filter((s) => s.estatus === pendiente).map((s) => Number(s[id]) || 0),
        };
      }),
    );
  }

  /**
   * Lo mismo que cuenta el tablero «Mis tickets»: del área del usuario,
   * sus columnas «Abiertas» (trae también las reabiertas) y «Proceso»
   * siguen en curso, y «Atendidas» espera su Vo.Bo. De quién son lo pone
   * el servidor con la sesión.
   */
  private tickets(): Observable<Seguimiento> {
    return this.pide<AreasUsuario | null>(UrlConstantes.consultarAreaUser, {}).pipe(
      switchMap((areas) => {
        const idArea = areas?.list?.[0]?.id;
        if (idArea === undefined || idArea === null) {
          // Sin área, su tablero sale vacío.
          return of<number[][]>([[], [], []]);
        }
        return forkJoin(
          [TicketsEstatus.ABIERTA, TicketsEstatus.PROCESO, TicketsEstatus.ATENDIDA].map((estatus) => this.columna(estatus, idArea)),
        );
      }),
      map(([abiertas, proceso, atendidas]) => ({ enCurso: abiertas.length + proceso.length, esperan: atendidas })),
    );
  }

  /** Los tickets de una columna del tablero, por su número. */
  private columna(estatus: TicketsEstatus, idArea: number): Observable<number[]> {
    return this.pide<TicketsPorEstatus | null>(UrlConstantes.obtenerTicketPorEstatusYUsuario, { id_estatus: estatus, id_area: idArea }).pipe(
      map((res) => {
        const codigo = res?.pCodRetorno;
        if (codigo === undefined || codigo === null || Number(codigo) !== 0) {
          throw new Error("Los tickets no llegaron");
        }
        const lista = res?.listaDeTiCkets;
        return Array.isArray(lista) ? lista.map((t) => Number(t?.id_ticket) || 0) : [];
      }),
    );
  }
}
